import React, { useState } from "react"
import ReactApexChart from "react-apexcharts"
import { FaChevronDown, FaChevronUp } from "react-icons/fa"

const PatientSummary = ({ day }) => {
	const [showDetailTable, setShowDetailTable] = useState(true)
	const [showAcuityChart, setShowAcuityChart] = useState(false)

	const scheduled = day.metrics?.current_scheduled_patients || day.totalScheduledPatients || 0
	const scheduledAM = day.metrics?.scheduled_patients_am || 0
	const scheduledPM = day.metrics?.scheduled_patients_pm || 0
	const target = day.metrics?.target_patients || 0
	const acuityDistribution = day.metrics?.acuity_distribution || {}

	// Get schedule data for the table
	const scheduleData = day.graphData?.utilizationCurves?.scheduleData || []

	// Helper function to format time from minutes to HH:MM AM/PM
	const formatTime = minutes => {
		const hours = Math.floor(minutes / 60)
		const mins = minutes % 60
		const period = hours >= 12 ? "PM" : "AM"
		const displayHour = hours % 12 === 0 ? 12 : hours % 12
		return `${displayHour}:${mins.toString().padStart(2, "0")} ${period}`
	}

	// Process schedule data for table display
	const tableData = scheduleData
		.map(slot => ({
			time: slot.time,
			timeFormatted: formatTime(slot.time),
			starts: slot.starts || 0,
			ongoing: slot.ongoing || 0
		}))
		.sort((a, b) => a.time - b.time) // Sort by time

	// Convert acuity distribution object to arrays for the chart
	const categories = Object.keys(acuityDistribution).map(key => `Acuity ${key}`)
	const values = Object.values(acuityDistribution)
	const total = values.reduce((sum, val) => sum + val, 0)

	const chartOptions = {
		chart: {
			type: "bar",
			height: 250,
			toolbar: {
				show: false
			},
			fontFamily: "Manrope, sans-serif"
		},
		plotOptions: {
			bar: {
				horizontal: true,
				barHeight: "75%",
				distributed: false,
				borderRadius: 8,
				columnWidth: "60%"
			}
		},
		colors: ["#6955EA", "#352D66", "#8B7FE5", "#A99FE8", "#C5BEF0"],
		stroke: {
			width: 1,
			colors: ["#fff"]
		},
		xaxis: {
			categories: categories,
			labels: {
				style: {
					fontFamily: "Manrope, sans-serif",
					fontSize: "12px"
				}
			}
		},
		yaxis: {
			labels: {
				style: {
					fontFamily: "Manrope, sans-serif",
					fontSize: "12px"
				}
			}
		},
		tooltip: {
			y: {
				formatter: function (val) {
					const percentage = ((val / total) * 100).toFixed(1)
					return `${val} patients (${percentage}%)`
				}
			}
		}
	}

	const series = [
		{
			name: "Patients",
			data: values
		}
	]

	return (
		<div className="bg-white rounded-lg shadow p-6 flex flex-col">
			<h3 className="text-lg font-semibold mb-4">Patient Summary</h3>
			<div className="flex flex-col space-y-2">
				<div className="flex justify-between">
					<span>Scheduled</span>
					<span className="font-bold">
						{scheduled}
						{
							<span className="text-sm font-normal text-gray-600 ml-2">
								• {scheduledAM} AM • {scheduledPM} PM
							</span>
						}
					</span>
				</div>
				<div className="flex justify-between">
					<span>Target</span>
					<span className="font-bold">{target}</span>
				</div>
				<hr className="my-2" />
			</div>

			{/* Toggle button for patient detail table */}
			<div className="mt-4">
				<button
					onClick={() => setShowDetailTable(!showDetailTable)}
					className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
				>
					<span className="text-sm font-medium text-gray-700">
						Hourly Utilization Table
					</span>
					{showDetailTable ? (
						<FaChevronUp className="text-gray-500" />
					) : (
						<FaChevronDown className="text-gray-500" />
					)}
				</button>

				{/* Collapsible table */}
				{showDetailTable && (
					<div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
						<div className="max-h-64 overflow-y-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50 sticky top-0">
									<tr>
										<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Time
										</th>
										<th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
											Starts
										</th>
										<th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
											Ongoing
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{tableData.length > 0 ? (
										tableData.map((slot, index) => (
											<tr
												key={slot.time}
												className={
													index % 2 === 0 ? "bg-white" : "bg-gray-50"
												}
											>
												<td className="px-3 py-2 text-sm text-gray-900 font-medium">
													{slot.timeFormatted}
												</td>
												<td className="px-3 py-2 text-sm text-center text-gray-600">
													{slot.starts}
												</td>
												<td className="px-3 py-2 text-sm text-center text-gray-600">
													{slot.ongoing}
												</td>
											</tr>
										))
									) : (
										<tr>
											<td
												colSpan="3"
												className="px-3 py-4 text-sm text-gray-500 text-center"
											>
												No schedule data available
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>

			{/* Toggle button for acuity distribution */}
			<div className="mt-4">
				<button
					onClick={() => setShowAcuityChart(!showAcuityChart)}
					className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
				>
					<span className="text-sm font-medium text-gray-700">Acuity Distribution</span>
					{showAcuityChart ? (
						<FaChevronUp className="text-gray-500" />
					) : (
						<FaChevronDown className="text-gray-500" />
					)}
				</button>

				{/* Collapsible acuity chart */}
				{showAcuityChart && (
					<div className="mt-3">
						{Object.keys(acuityDistribution).length > 0 ? (
							<ReactApexChart
								options={chartOptions}
								series={series}
								type="bar"
								height={250}
							/>
						) : (
							<div className="text-gray-500 text-center py-4">
								No acuity data available
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export default PatientSummary
